import { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import Popover from './Popover';
import { VACIO, ETIQUETA_VACIO } from './filtros';

/**
 * Filtro de una columna: texto (contiene), multiselección con valores reales y
 * recuentos (facetas del servidor, nunca una lista fija) o rango de fechas.
 */
const SIN_FACETAS = () => Promise.resolve([]);

const FiltroColumna = ({
  columna,
  ancla = null,
  valorActual = null,
  loadFacets = SIN_FACETAS,
  onApply,
  onClose,
}) => {
  const tipo = columna.filter;
  const [texto, setTexto] = useState(valorActual?.texto || '');
  const [desde, setDesde] = useState(valorActual?.desde || '');
  const [hasta, setHasta] = useState(valorActual?.hasta || '');
  const [seleccion, setSeleccion] = useState(() => new Set(valorActual?.valores || []));
  const [busqueda, setBusqueda] = useState('');
  const [facetas, setFacetas] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (tipo !== 'multi') return undefined;
    let vivo = true;
    Promise.resolve(loadFacets(columna.id))
      .then((v) => vivo && setFacetas(v || []))
      .catch(() => vivo && setError(true));
    return () => {
      vivo = false;
    };
  }, [tipo, columna.id, loadFacets]);

  // Los valores ya elegidos siguen visibles aunque el filtro actual ya no los devuelva.
  const opciones = useMemo(() => {
    if (!facetas) return [];
    const conocidos = new Set(facetas.map((f) => f.valor));
    const extra = [...seleccion].filter((v) => !conocidos.has(v)).map((v) => ({ valor: v, n: 0 }));
    const q = busqueda.trim().toLowerCase();
    return [...facetas, ...extra].filter(
      (f) => !q || (f.valor === VACIO ? ETIQUETA_VACIO : f.valor).toLowerCase().includes(q),
    );
  }, [facetas, seleccion, busqueda]);

  const alternar = (valor) =>
    setSeleccion((prev) => {
      const sig = new Set(prev);
      if (sig.has(valor)) sig.delete(valor);
      else sig.add(valor);
      return sig;
    });

  const marcarVisibles = (marcar) =>
    setSeleccion((prev) => {
      const sig = new Set(prev);
      opciones.forEach((o) => (marcar ? sig.add(o.valor) : sig.delete(o.valor)));
      return sig;
    });

  const aplicar = () => {
    if (tipo === 'multi') onApply({ valores: [...seleccion] });
    else if (tipo === 'date') onApply({ desde: desde || undefined, hasta: hasta || undefined });
    else onApply({ texto: texto.trim() });
    onClose();
  };

  const limpiar = () => {
    onApply(null);
    onClose();
  };

  return (
    <Popover
      ancla={ancla}
      onClose={onClose}
      ancho={tipo === 'multi' ? 300 : 260}
      etiqueta={`Filtrar ${columna.label}`}
    >
      <div className="tbl-pop-titulo">Filtrar · {columna.label}</div>

      {tipo === 'text' && (
        <input
          className="tbl-input"
          type="text"
          placeholder="Contiene…"
          aria-label={`Texto que contiene ${columna.label}`}
          value={texto}
          maxLength={100}
          // eslint-disable-next-line jsx-a11y/no-autofocus
          autoFocus
          onChange={(e) => setTexto(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && aplicar()}
        />
      )}

      {tipo === 'date' && (
        <div className="tbl-fechas">
          <label>
            Desde
            <input
              className="tbl-input"
              type="date"
              value={desde}
              onChange={(e) => setDesde(e.target.value)}
            />
          </label>
          <label>
            Hasta
            <input
              className="tbl-input"
              type="date"
              value={hasta}
              onChange={(e) => setHasta(e.target.value)}
            />
          </label>
        </div>
      )}

      {tipo === 'multi' && (
        <>
          <input
            className="tbl-input"
            type="search"
            placeholder="Buscar valor…"
            aria-label="Buscar valor"
            value={busqueda}
            // eslint-disable-next-line jsx-a11y/no-autofocus
            autoFocus
            onChange={(e) => setBusqueda(e.target.value)}
          />
          <div className="tbl-pop-acciones">
            <button type="button" className="tbl-link" onClick={() => marcarVisibles(true)}>
              Seleccionar todo
            </button>
            <button type="button" className="tbl-link" onClick={() => marcarVisibles(false)}>
              Ninguno
            </button>
          </div>
          <div className="tbl-lista" role="group" aria-label={`Valores de ${columna.label}`}>
            {facetas === null && !error && <div className="tbl-vacio-txt">Cargando valores…</div>}
            {error && <div className="tbl-vacio-txt">No se pudieron cargar los valores.</div>}
            {facetas !== null && opciones.length === 0 && (
              <div className="tbl-vacio-txt">Sin valores.</div>
            )}
            {opciones.map((o) => (
              <label key={o.valor} className="tbl-opcion">
                <input
                  type="checkbox"
                  checked={seleccion.has(o.valor)}
                  onChange={() => alternar(o.valor)}
                />
                <span className="tbl-opcion-txt">
                  {o.valor === VACIO ? ETIQUETA_VACIO : o.valor}
                </span>
                <span className="tbl-opcion-n">{o.n}</span>
              </label>
            ))}
          </div>
        </>
      )}

      <div className="tbl-pop-pie">
        <button type="button" className="tbl-btn" onClick={limpiar}>
          Limpiar
        </button>
        <button type="button" className="tbl-btn tbl-btn-primario" onClick={aplicar}>
          Aplicar
        </button>
      </div>
    </Popover>
  );
};

FiltroColumna.propTypes = {
  columna: PropTypes.shape({
    id: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    filter: PropTypes.oneOf(['text', 'multi', 'date']).isRequired,
  }).isRequired,
  ancla: PropTypes.object,
  valorActual: PropTypes.object,
  loadFacets: PropTypes.func,
  onApply: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default FiltroColumna;
